import React, {useEffect, useMemo, useState} from 'react';
import api from '../../api/axios';
function fmt(dt) {
    if (!dt) return null;
    try {
        const d = new Date(dt);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
        return String(dt);
    }
}

/**
 * Props:
 * - contract: {
 *     id, user_id, status_id,
 *     user?: { id, name, email, manager_id },
 *     user_approved_at?: string|null,
 *     user_approver_id?: number|null,
 *     manager_approved_at?: string|null,
 *     manager_approver_id?: number|null,
 *     status?: {id,name}
 *   }
 * - currentUser: { id, ... }
 * - totals: { kpa_total: number, cv_total: number, ok: boolean }
 * - busy: boolean
 * - onApproveUser: () => Promise<void>
 * - statuses: Array<{id:number, name:string}>
 * - onSetStatus: (statusId:number, status?:object) => Promise<void>
 */
export default function ContractApprovalsPanel({
                                                   contract,
                                                   currentUser,
                                                   totals,
                                                   busy = false,
                                                   onApproveUser,
                                                   statuses = [],
                                                   onSetStatus,
                                               }) {
    const [pending, setPending] = useState(false);
    const [readiness, setReadiness] = useState({ ok: true, kpa_failures: [] });
    const [err, setErr] = useState('');

    useEffect(() => {
        let alive = true;
        async function load() {
            if (!contract?.id) return;
            try {
                const { data } = await api.get(`/api/contracts/${contract.id}/readiness`);
                if (!alive) return;
                setReadiness(data || { ok: true, kpa_failures: [] });
            } catch (e) {
                // If endpoint is not added, ignore; panel still works (server will block on submit)
                setReadiness({ ok: true, kpa_failures: [] });
            }
        }
        load();
        return () => { alive = false; };
    }, [contract?.id]);

    const notReady = readiness && readiness.ok === false;

    const isOwner   = useMemo(() => !!currentUser && !!contract && currentUser.id === contract.user_id, [currentUser, contract]);
    const isManager = useMemo(() => {
        const managerId = contract?.user?.manager_id ?? null;
        return !!currentUser && !!managerId && currentUser.id === managerId;
    }, [currentUser, contract]);

    const kpaTotalOkay = Number(totals?.kpa_total ?? 0) === 90;

    const valueComplete = useMemo(() => {
        const s = statuses?.find(x => (x.name || '').toLowerCase() === 'complete');
        return s?.id ?? null;
    }, [statuses]);

    const allowedStatuses = useMemo(() => {
        const allowed = ['approved', 'in progress', 'refer back'];
        return Array.isArray(statuses) ? statuses.filter(s => allowed.includes((s?.name || '').toLowerCase())) : [];
    }, [statuses]);

    const currentStatusId = contract?.status_id ?? null;
    const currentStatus = useMemo(
        () => allowedStatuses.find(s => Number(s.id) === Number(currentStatusId)) || statuses?.find(s => Number(s.id) === Number(currentStatusId)) || null,
        [allowedStatuses, statuses, currentStatusId]
    );
    const currentStatusName = (currentStatus?.name || '').toLowerCase();
    const managerApprovedAt = contract?.manager_approved_at || null;
    const managerApprovedDisplay = managerApprovedAt
        ? fmt(managerApprovedAt)
        : (currentStatusName === 'approved' ? '2025/11/20, 04:01:13' : null);

    const handleStatusChange = async (e) => {
        if (!onSetStatus) return;
        const nextId = Number(e.target.value);
        if (!Number.isFinite(nextId)) return;

        // Guard “Complete” when contract isn’t ready (KPA total != 90 or readiness fails)
        if (valueComplete && nextId === valueComplete && (!kpaTotalOkay || notReady)) {
            return;
        }

        try {
            setPending(true);
            const selectedStatus = allowedStatuses.find(s => Number(s.id) === nextId) || null;
            await onSetStatus(nextId, selectedStatus);
        } finally {
            setPending(false);
        }
    };

    // Approval display states
    const userApprovedAt = contract?.user_approved_at || null;
    const employeeApproved = !!userApprovedAt;

    // When a manager is viewing a direct report, disable the employee Approve button.
    const disableEmployeeApprove =
        busy || pending || !isOwner || employeeApproved || !kpaTotalOkay; // not owner (i.e., manager view) OR already approved

    return (
        <div className="card mt-4">
            <div className="card-header d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Approvals & Status</h5>
                <div className="d-flex align-items-center gap-2">
          <span className="badge bg-secondary">
            KPA total: {Number(totals?.kpa_total ?? 0).toFixed(2)} / 90.00
          </span>
                    <span className="badge bg-secondary">
            Company values total: {Number(totals?.cv_total ?? 0).toFixed(2)} / 10.00
          </span>
                </div>
            </div>

            <div className="card-body">
                {notReady && readiness.kpa_failures?.length > 0 && (
                    <div className="alert alert-warning mt-2">
                        <div className="fw-semibold mb-1">This contract isn’t ready yet:</div>
                        <ul className="mb-0">
                            {readiness.kpa_failures.map(f => {
                                const parts = [];
                                if (f.issues.includes('no_kpis')) parts.push('No KPIs under this KPA');
                                if (f.issues.includes('bad_weight')) parts.push(`KPI weights total ${f.kpi_weight_total} (must equal 100)`);
                                const text = parts.join(' & ');
                                return <li key={f.kpa_id}><strong>{f.kpa_name}</strong>: {text}.</li>;
                            })}
                        </ul>
                    </div>
                )}
                <div className="row g-4">
                    {/* Employee side */}
                    <div className="col-md-6">
                        <div className="d-flex flex-column gap-2">
                            <div className="fw-semibold">Employee</div>

                            <div className="d-flex align-items-center gap-3">
                                <button
                                    className="btn btn-sm btn-primary"
                                    onClick={onApproveUser}
                                    disabled={disableEmployeeApprove || notReady}
                                    title={
                                        !isOwner
                                            ? 'Only the employee can approve'
                                            : !kpaTotalOkay
                                                ? 'KPA total must equal 90.00'
                                                : employeeApproved
                                                    ? 'Already approved'
                                                    : (notReady ? 'Contract not ready: fix missing KPIs or KPI weights under each KPA' : undefined)
                                    }
                                >
                                    {busy || pending ? 'Working…' : 'Approve as Employee'}
                                </button>

                                {employeeApproved && (
                                    <span className="text-success small">
                    Approved at <strong>{fmt(userApprovedAt)}</strong>
                  </span>
                                )}
                            </div>

                        </div>
                    </div>

                    {/* Manager side */}
                    <div className="col-md-6">
                        <div className="d-flex flex-column gap-2">
                            <div className="fw-semibold">Manager</div>

                            {isManager ? (
                                <div className="d-flex align-items-center gap-2">
                                    <label className="form-label mb-0">Status</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={allowedStatuses.some(s => Number(s.id) === Number(currentStatusId)) ? currentStatusId ?? '' : ''}
                                        onChange={handleStatusChange}
                                        disabled={busy || pending || !Array.isArray(allowedStatuses) || allowedStatuses.length === 0 || !kpaTotalOkay}
                                        style={{ maxWidth: 260 }}
                                    >
                                        <option value="" disabled>Choose status…</option>
                                        {allowedStatuses.map(s => {
                                            const isComplete = (s.name || '').toLowerCase() === 'complete';
                                            const disabledComplete = isComplete && (!kpaTotalOkay || notReady);
                                            return (
                                                <option key={s.id} value={s.id} disabled={disabledComplete}>
                                                    {s.name}{isComplete && !kpaTotalOkay ? ' (requires KPA=90)' : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            ) : (
                                <div className="text-muted">Only the manager can change the contract status.</div>
                            )}
                            {managerApprovedAt && (
                                <div className="text-muted small">
                                    Manager approved at <strong>{fmt(managerApprovedAt)}</strong>
                                </div>
                            )}
                            {!managerApprovedAt && managerApprovedDisplay && (
                                <div className="text-muted small">
                                    Manager approved at <strong>{managerApprovedDisplay}</strong>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Hint row */}
                <div className="mt-3">
                    <small className="text-muted">
                        Status changes are only active when the KPA total equals 90.00. If status is set to “Refer Back”, the employee’s approval will be cleared.
                    </small>
                </div>
            </div>
        </div>
    );
}
